import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useFetch } from "@/lib/api";
import { PageHeader, StatCard, SectionTitle, Loading, ScoreChip } from "@/components/common";
import TokenTable from "@/components/TokenTable";
import LiveActivityStrip from "@/components/LiveActivityStrip";
import { fmtUsd, fmtNum, fmtPct, timeAgo } from "@/lib/format";
import {
  LayoutDashboard, TrendingUp, Droplets, Users, Activity, Rocket,
  Hammer, Sparkles, ShieldAlert, ArrowUpRight,
} from "lucide-react";

const SORTS = [
  ["momentum", "Momentum"],
  ["newest", "Newest"],
  ["organic", "Organic"],
  ["volume", "Volume"],
  ["holder_growth", "Holder Growth"],
  ["liquidity", "Liquidity"],
  ["market_cap", "Market Cap"],
  ["risk", "Risk"],
  ["tracked_wallets", "Tracked Wallets"],
];

export default function CommandCenter() {
  const nav = useNavigate();
  const [sort, setSort] = useState("momentum");
  const { data: market } = useFetch("/market/overview", []);
  const { data: tokens } = useFetch(`/tokens?sort=${sort}&limit=40`, [sort]);
  const { data: devs } = useFetch("/developers?sort=launches&limit=5", []);
  const { data: narratives } = useFetch("/narratives", []);
  const { data: risky } = useFetch("/tokens?sort=risk&limit=5", []);

  const m = market?.data;
  if (!m) return <Loading />;

  return (
    <div className="fade-in">
      <PageHeader
        icon={LayoutDashboard}
        title="Command Center"
        subtitle="What is happening right now across the Solana memecoin market. Click anything to investigate it immediately."
      />

      <LiveActivityStrip />

      <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
        <StatCard testid="stat-sol" label="SOL Price" value={fmtUsd(m.sol_price)} sub={fmtPct(m.sol_change_24h)} tone={m.sol_change_24h >= 0 ? "pos" : "neg"} icon={TrendingUp} />
        <StatCard testid="stat-new-5m" label="New Pairs 5m" value={m.new_pairs_5m} sub={`${m.new_pairs_1h}/hr`} icon={Rocket} />
        <StatCard testid="stat-newpairs" label="New Pairs 24h" value={fmtNum(m.total_new_pairs_24h)} sub={`${m.new_pairs_15m} last 15m`} icon={Activity} />
        <StatCard testid="stat-liq" label="Avg Liquidity" value={fmtUsd(m.avg_liquidity)} icon={Droplets} />
        <StatCard testid="stat-vol" label="Volume 24h" value={fmtUsd(m.total_volume_24h)} sub={`accel ${fmtPct(m.activity_acceleration)}`} tone={m.activity_acceleration >= 0 ? "pos" : "neg"} icon={Activity} />
        <StatCard testid="stat-traders" label="Active Traders" value={fmtNum(m.active_traders)} sub={`${m.milestone_breakers} MC breakers`} icon={Users} />
      </div>

      <div className="grid gap-5 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <SectionTitle
            right={
              <div className="flex items-center gap-1.5">
                <span className="live-dot" />
                <select
                  data-testid="opportunity-sort"
                  value={sort}
                  onChange={(e) => setSort(e.target.value)}
                  className="rounded-md border border-slate-800 bg-panel px-2 py-1 text-xs text-slate-300 outline-none"
                >
                  {SORTS.map(([v, l]) => (
                    <option key={v} value={v}>{l}</option>
                  ))}
                </select>
              </div>
            }
          >
            Opportunity Feed
          </SectionTitle>
          {tokens ? <TokenTable tokens={tokens.data} /> : <Loading />}
        </div>

        <div className="space-y-5">
          <div className="pnut-card p-4">
            <SectionTitle right={<Hammer className="h-4 w-4 text-slate-500" />}>Recent Dev Launches</SectionTitle>
            <div className="space-y-2">
              {devs?.data?.map((d) => (
                <button
                  key={d.address}
                  onClick={() => nav(`/developer/${d.address}`)}
                  className="flex w-full items-center justify-between rounded-md border border-slate-800/60 bg-void/40 px-3 py-2 text-left hover:border-amber-600/30"
                >
                  <div>
                    <div className="font-mono text-xs text-slate-200">{d.short}</div>
                    <div className="text-[10px] text-slate-500">{d.launches_count} launches · {d.reached_1m} hit $1M+</div>
                  </div>
                  <ArrowUpRight className="h-4 w-4 text-slate-600" />
                </button>
              ))}
            </div>
          </div>

          <div className="pnut-card p-4">
            <SectionTitle right={<Sparkles className="h-4 w-4 text-slate-500" />}>Emerging Narratives</SectionTitle>
            <div className="space-y-2">
              {narratives?.data?.slice(0, 5).map((n) => (
                <button
                  key={n.id}
                  onClick={() => nav("/narrative-radar")}
                  className="flex w-full items-center justify-between rounded-md border border-slate-800/60 bg-void/40 px-3 py-2 text-left hover:border-amber-600/30"
                >
                  <div>
                    <div className="text-xs font-semibold text-slate-200">{n.name}</div>
                    <div className="text-[10px] text-slate-500">{n.stage} · {n.tokens_count} tokens</div>
                  </div>
                  <ScoreChip type="momentum" value={n.narrative_score} />
                </button>
              ))}
            </div>
          </div>

          <div className="pnut-card p-4">
            <SectionTitle right={<ShieldAlert className="h-4 w-4 text-rose-500" />}>Security Warnings</SectionTitle>
            <div className="space-y-2">
              {risky?.data?.filter((t) => t.risk_score > 55).slice(0, 5).map((t) => (
                <button
                  key={t.id}
                  onClick={() => nav(`/token/${t.id}`)}
                  className="flex w-full items-center justify-between rounded-md border border-rose-900/40 bg-rose-950/10 px-3 py-2 text-left hover:border-rose-700/50"
                >
                  <div>
                    <div className="text-xs font-semibold text-slate-200">${t.symbol}</div>
                    <div className="text-[10px] text-slate-500">{timeAgo(t.age_minutes)} · {fmtUsd(t.market_cap)}</div>
                  </div>
                  <ScoreChip type="risk" value={t.risk_score} />
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
